import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "roster-panel" }
const _hoisted_2 = {
  key: 0,
  class: "text-muted small mt-2"
}
const _hoisted_3 = {
  key: 1,
  class: "list-group list-group-flush mt-2"
}
const _hoisted_4 = { class: "d-flex align-items-center" }
const _hoisted_5 = ["name", "checked", "disabled", "onChange"]
const _hoisted_6 = {
  key: 0,
  class: "spinner-border spinner-border-sm text-secondary ms-1",
  role: "status",
  "aria-hidden": "true"
}
const _hoisted_7 = { class: "flex-grow-1" }
const _hoisted_8 = {
  key: 0,
  class: "text-muted small ms-1"
}
const _hoisted_9 = { class: "form-check form-switch mb-0 d-flex align-items-center" }
const _hoisted_10 = ["id", "checked", "disabled", "onChange"]
const _hoisted_11 = ["for"]
const _hoisted_12 = {
  key: 0,
  class: "spinner-border spinner-border-sm text-secondary ms-1",
  role: "status",
  "aria-hidden": "true"
}
const _hoisted_13 = {
  key: 0,
  class: "badge bg-success rounded-pill ms-1"
}
const _hoisted_14 = { class: "mt-2" }
const _hoisted_15 = {
  key: 2,
  class: "mt-2 border rounded p-2"
}

import { ref, onMounted } from 'vue'
import VehicleLookupCard from '@/components/vehicles/VehicleLookupCard.vue'
import type { VehiclePayload } from '@/types/VehiclePayload'
import DriverVehicleRepository from '@/repositories/DriverVehicleRepository'
import { DriverVehicleLink } from '@/types/Vehicle'
import ToastService from '@/services/ToastService'
import i18n from '@/plugins/i18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'RosterPanel',
  props: {
    driverId: {}
  },
  setup(__props: any) {

const props = __props

const links = ref<DriverVehicleLink[]>([])
const showLookup = ref(false)
const savingSelectable = ref<Set<string>>(new Set())
const savingSelected = ref<Set<string>>(new Set())

async function fetchLinks(): Promise<void> {
  try {
    links.value = await DriverVehicleRepository.listForDriver(props.driverId)
  } catch (e: unknown) {
    const err = e as Error
    await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), err.message)
  }
}

async function onToggleSelectable(vehicleId: string, selectable: boolean): Promise<void> {
  if (savingSelectable.value.has(vehicleId)) return
  savingSelectable.value.add(vehicleId)
  try {
    await DriverVehicleRepository.setSelectable(props.driverId, vehicleId, selectable)
    await fetchLinks()
    await ToastService.toast(ToastService.SUCCESS, i18n.global.t('common.messages.updated'))
  } catch (e: unknown) {
    const err = e as Error
    // Switch is bound one-way (:checked), so the browser already flipped it on click;
    // re-fetch to force the visual back to the persisted state.
    await fetchLinks()
    await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), err.message)
  } finally {
    savingSelectable.value.delete(vehicleId)
  }
}

async function onSelectVehicle(vehicleId: string): Promise<void> {
  if (savingSelected.value.has(vehicleId)) return
  savingSelected.value.add(vehicleId)
  try {
    await DriverVehicleRepository.setSelected(props.driverId, vehicleId)
    await fetchLinks()
    await ToastService.toast(ToastService.SUCCESS, i18n.global.t('common.messages.updated'))
  } catch (e: unknown) {
    const err = e as Error
    await fetchLinks()
    await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), err.message)
  } finally {
    savingSelected.value.delete(vehicleId)
  }
}

async function onVehicleSelected(payload: VehiclePayload): Promise<void> {
  try {
    await DriverVehicleRepository.link(props.driverId, { vehicleId: payload.vehicleId })
    showLookup.value = false
    await fetchLinks()
    await ToastService.toast(ToastService.SUCCESS, i18n.global.t('vehicles.messages.vehicle_added'))
  } catch (e: unknown) {
    const axiosErr = e as { response?: { status?: number; data?: { error?: string } } }
    if (axiosErr?.response?.status === 409 && axiosErr.response?.data?.error === 'link_already_exists') {
      showLookup.value = false
      await fetchLinks()
      return
    }
    const err = e as Error
    await ToastService.toast(ToastService.ERROR, i18n.global.t('common.messages.error'), err.message)
  }
}

onMounted(fetchLinks)

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (links.value.length === 0 && !showLookup.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_ctx.$t('vehicles.messages.no_linked_drivers')), 1))
      : _createCommentVNode("", true),
    (links.value.length > 0)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(links.value, (link) => {
            return (_openBlock(), _createElementBlock("li", {
              key: link.vehicle_id,
              class: "list-group-item d-flex align-items-center gap-2 px-0 py-1"
            }, [
              _createElementVNode("span", _hoisted_4, [
                _createElementVNode("input", {
                  type: "radio",
                  name: `selected-vehicle-${__props.driverId}`,
                  checked: link.is_selected,
                  disabled: savingSelected.value.has(link.vehicle_id),
                  onChange: ($event: any) => (onSelectVehicle(link.vehicle_id)),
                  class: "form-check-input mt-0",
                  title: "Set as selected vehicle"
                }, null, 40, _hoisted_5),
                (savingSelected.value.has(link.vehicle_id))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("span", _hoisted_7, [
                _createVNode(_component_router_link, {
                  to: { name: 'vehicles.detail', params: { id: link.vehicle_id } },
                  class: "fw-bold text-decoration-none"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(link.vehicle.plate), 1)
                  ]),
                  _: 2
                }, 1032, ["to"]),
                (link.vehicle.brand)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(link.vehicle.brand), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("input", {
                  class: "form-check-input",
                  type: "checkbox",
                  id: `selectable-${link.vehicle_id}`,
                  checked: link.selectable,
                  disabled: savingSelectable.value.has(link.vehicle_id),
                  onChange: ($event: any) => (onToggleSelectable(link.vehicle_id, !link.selectable))
                }, null, 40, _hoisted_10),
                _createElementVNode("label", {
                  class: "form-check-label small",
                  for: `selectable-${link.vehicle_id}`
                }, _toDisplayString(_ctx.$t('vehicles.fields.selectable')), 9, _hoisted_11),
                (savingSelectable.value.has(link.vehicle_id))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_12))
                  : _createCommentVNode("", true)
              ]),
              (link.is_active)
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_ctx.$t('common.fields.enabled')), 1))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_14, [
      _createElementVNode("button", {
        type: "button",
        class: "btn btn-sm btn-outline-primary",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (showLookup.value = !showLookup.value))
      }, " + " + _toDisplayString(_ctx.$t('vehicles.actions.add_vehicle')), 1)
    ]),
    (showLookup.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
          _createVNode(VehicleLookupCard, { onSelect: onVehicleSelected })
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})